import { nanoid } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowBackIos, ArrowDropDown, ArrowForwardIos } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { EAllowedDateDirection } from '../types';

const today = new Date();
today.setHours(0, 0, 0, 0);

/** How far the year list reaches around the current year when nothing else limits it. */
const YEARS_BACK = 30;
const YEARS_AHEAD = 10;

const MONTH_NAMES = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

/** The header label switches the calendar body between the days, the months and the years. */
type TCalendarView = 'day' | 'month' | 'year';

interface CalendarProps {
	displayDate: Date;
	startDate: Date | null;
	endDate: Date | null;
	onDateSelect: (date: Date) => void;
	onMonthChange: (newDate: Date) => void;
	allowedDateDirection?: EAllowedDateDirection;
	side?: 'left' | 'right' | 'both';
}

export const Calendar: React.FC<CalendarProps> = ({
	displayDate,
	startDate,
	endDate,
	onDateSelect,
	onMonthChange,
	allowedDateDirection = EAllowedDateDirection.ALL,
	side = 'both',
}) => {
	const year = displayDate.getFullYear();
	const month = displayDate.getMonth();

	const [view, setView] = useState<TCalendarView>('day');
	const yearsRef = useRef<HTMLDivElement | null>(null);
	const selectedYearRef = useRef<HTMLDivElement | null>(null);

	// Keep the year of the displayed month visible as soon as the year list opens. The scroll is
	// set on the list itself so that the dialog around the calendar stays where it is.
	useEffect(() => {
		if (view !== 'year') return;
		const list = yearsRef.current;
		const selected = selectedYearRef.current;
		if (!list || !selected) return;
		list.scrollTop = selected.offsetTop - list.clientHeight / 2 + selected.clientHeight / 2;
	}, [view]);

	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const firstDayOfMonth = new Date(year, month, 1).getDay();
	const adjustedFirstDay = (firstDayOfMonth === 0 ? 7 : firstDayOfMonth) - 1;

	const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
	const blanks = Array(adjustedFirstDay).fill(null);

	const handlePrevMonth = () => {
		onMonthChange(new Date(year, month - 1, 1));
	};

	const handleNextMonth = () => {
		onMonthChange(new Date(year, month + 1, 1));
	};

	const currentYear = today.getFullYear();
	const minYear = Math.min(
		allowedDateDirection === EAllowedDateDirection.FUTURE ? currentYear : currentYear - YEARS_BACK,
		year,
	);
	const maxYear = Math.max(
		allowedDateDirection === EAllowedDateDirection.PAST ? currentYear : currentYear + YEARS_AHEAD,
		year,
	);
	const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

	const isYearDisabled = (value: number): boolean => {
		if (allowedDateDirection === EAllowedDateDirection.PAST) {
			// The current year still holds the days before today.
			return value > currentYear;
		}
		if (allowedDateDirection === EAllowedDateDirection.FUTURE) {
			return value < currentYear;
		}
		return false;
	};

	/** A month is only closed off when every single one of its days is. */
	const isMonthDisabled = (value: number): boolean => {
		const monthStart = new Date(year, value, 1);
		monthStart.setHours(0, 0, 0, 0);
		const monthEnd = new Date(year, value + 1, 0);
		monthEnd.setHours(0, 0, 0, 0);

		if (allowedDateDirection === EAllowedDateDirection.PAST) {
			return monthStart.getTime() >= today.getTime();
		}
		if (allowedDateDirection === EAllowedDateDirection.FUTURE) {
			return monthEnd.getTime() <= today.getTime();
		}
		return false;
	};

	const handleYearSelect = (value: number) => {
		if (isYearDisabled(value)) return;
		// The month is kept, the picker just moves on to it — as the MUI pickers do.
		onMonthChange(new Date(value, month, 1));
		setView('month');
	};

	const handleMonthSelect = (value: number) => {
		if (isMonthDisabled(value)) return;
		onMonthChange(new Date(year, value, 1));
		setView('day');
	};

	const isStart = (day: number) => {
		if (!startDate) return false;
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const startDateTime = new Date(startDate).setHours(0, 0, 0, 0);
		return currentDate.getTime() === startDateTime;
	};

	const isEnd = (day: number) => {
		if (!endDate) return false;
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const endDateTime = new Date(endDate).setHours(0, 0, 0, 0);
		return currentDate.getTime() === endDateTime;
	};

	const isToday = (day: number) => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		return currentDate.getTime() === today.getTime();
	};

	const isSelected = (day: number) => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const startDateTime = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
		const endDateTime = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;

		return (
			(startDateTime && currentDate.getTime() === startDateTime) ||
			(endDateTime && currentDate.getTime() === endDateTime)
		);
	};

	const isInRange = (day: number) => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const startDateTime = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
		const endDateTime = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;
		return (
			startDateTime &&
			endDateTime &&
			currentDate.getTime() > startDateTime &&
			currentDate.getTime() < endDateTime
		);
	};

	const isDisabled = (day: number): boolean => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);

		if (allowedDateDirection === EAllowedDateDirection.PAST) {
			return currentDate.getTime() >= today.getTime();
		}
		if (allowedDateDirection === EAllowedDateDirection.FUTURE) {
			return currentDate.getTime() <= today.getTime();
		}
		return false;
	};

	// TODO: Add styles for the calendar
	const cellStyle = (day: number): React.CSSProperties => {
		const baseStyle: React.CSSProperties = {
			padding: '10px',
			textAlign: 'center',
			cursor: 'pointer',
			border: '1px solid #eee',
			color: '#006293',
		};
		if (isSelected(day)) {
			return {
				...baseStyle,
				backgroundColor: '#006293',
				color: 'white',
				borderRadius: isStart(day) ? '12px 0 0 12px' : '0 12px 12px 0',
			};
		}

		if (isToday(day)) {
			return {
				...baseStyle,
				// backgroundColor: '#00629370',
				fontWeight: '900',
			};
		}

		if (isDisabled(day)) {
			return {
				...baseStyle,
				cursor: 'not-allowed',
				backgroundColor: '#00629310',
				color: '#00629340',
			};
		}
		if (isInRange(day)) {
			return { ...baseStyle, backgroundColor: '#00629340' };
		}

		return baseStyle;
	};

	/** Shared look of the year and the month cells of the picker. */
	const pickerCellStyle = (isActive: boolean, disabled: boolean): React.CSSProperties => ({
		padding: '10px 6px',
		textAlign: 'center',
		borderRadius: '18px',
		fontSize: '14px',
		cursor: disabled ? 'not-allowed' : 'pointer',
		backgroundColor: isActive ? '#006293' : 'transparent',
		color: disabled ? '#00629340' : isActive ? '#fff' : '#006293',
		fontWeight: isActive ? 700 : 400,
	});

	const weekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

	return (
		<div
			style={{
				width: '300px',
				// border: '1px solid #ccc',
				padding: '10px',
				borderRadius: '12px',
				backgroundColor: '#fff',
			}}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '10px',
				}}
			>
				{(side === 'left' || side === 'both') && view === 'day' ? (
					<IconButton onClick={handlePrevMonth}>
						<ArrowBackIos color="primary" />
					</IconButton>
				) : (
					<div style={{ width: '40px', height: '40px' }} />
				)}
				<div
					role="button"
					tabIndex={0}
					onClick={() => setView(view === 'day' ? 'year' : 'day')}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							setView(view === 'day' ? 'year' : 'day');
						}
					}}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '2px',
						cursor: 'pointer',
						userSelect: 'none',
						fontSize: '18px',
						fontWeight: 'bold',
						color: '#006293',
					}}
				>
					{view === 'year'
						? year
						: displayDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
					<ArrowDropDown
						color="primary"
						style={{
							transition: 'transform 0.2s',
							transform: view === 'day' ? 'rotate(0deg)' : 'rotate(180deg)',
						}}
					/>
				</div>
				{(side === 'right' || side === 'both') && view === 'day' ? (
					<IconButton onClick={handleNextMonth}>
						<ArrowForwardIos color="primary" />
					</IconButton>
				) : (
					<div style={{ width: '40px', height: '40px' }} />
				)}
			</div>
			{view === 'day' && (
				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
					{weekdays.map((day: string) => (
						<div
							key={nanoid()}
							style={{ fontWeight: 'bold', textAlign: 'center', color: '#006293' }}
						>
							{day}
						</div>
					))}
					{blanks.map(() => (
						<div key={nanoid()} />
					))}
					{days.map((day: number) => (
						<div
							key={day}
							style={cellStyle(day)}
							onClick={() => {
								const selectedDate = new Date(year, month, day);
								selectedDate.setHours(0, 0, 0, 0);
								if (!isDisabled(day)) {
									onDateSelect(selectedDate);
								}
							}}
						>
							{day}
						</div>
					))}
				</div>
			)}
			{view === 'year' && (
				<div
					ref={yearsRef}
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(3, 1fr)',
						gap: '4px',
						height: '288px',
						alignContent: 'start',
						overflowY: 'auto',
					}}
				>
					{years.map((value: number) => (
						<div
							key={value}
							ref={value === year ? selectedYearRef : undefined}
							style={pickerCellStyle(value === year, isYearDisabled(value))}
							onClick={() => handleYearSelect(value)}
						>
							{value}
						</div>
					))}
				</div>
			)}
			{view === 'month' && (
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(3, 1fr)',
						gap: '4px',
						height: '288px',
						alignContent: 'center',
					}}
				>
					{MONTH_NAMES.map((name: string, index: number) => (
						<div
							key={name}
							style={pickerCellStyle(index === month, isMonthDisabled(index))}
							onClick={() => handleMonthSelect(index)}
						>
							{name}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
